import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, CircularProgress, Alert, Stack, Divider,
  InputAdornment, Card, CardContent, CardHeader, Grid, MenuItem, Select,
  FormControl, InputLabel, LinearProgress, Collapse, Autocomplete,
} from '@mui/material';
import { staffService, StaffProfile } from '../../services/staffService';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Percent as PercentIcon, AttachMoney as MoneyIcon,
  People as PeopleIcon, Person as PersonIcon,
  Info as InfoIcon, ExpandMore, ExpandLess,
  CheckCircle as CheckIcon, AutoFixHigh as AutoCalcIcon,
  Hotel as HotelIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import PageLayout from '../../components/Layout/PageLayout';
import {
  BillingRate, UpdateBillingRateRequest,
  billingRatesService, StaffBaseRate, staffBaseRateService,
  ClientBaseRate, clientBaseRateService, SetStaffBaseRateRequest,
  SetClientBaseRateRequest, SERVICE_TYPE_LABELS,
  FlatRate, CreateFlatRateRequest, UpdateFlatRateRequest, flatRateService,
  FLAT_RATE_CATEGORIES,
  NdisSupportItem, NdisSupportItemRequest, ndisSupportItemService,
} from '../../services/newBillingService';

// ── Helpers ───────────────────────────────────────────────────────────────────

function minutesToTimeString(m: number) {
  if (m >= 1440) return '24:00';
  return `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
}

const DAY_LABELS: Record<string, string> = {
  Weekday: 'Weekday', Saturday: 'Saturday', Sunday: 'Sunday', PublicHoliday: 'Public Holiday',
};

const DAY_CHIP_COLOR: Record<string, 'default' | 'secondary' | 'warning' | 'error'> = {
  Weekday: 'default', Saturday: 'secondary', Sunday: 'warning', PublicHoliday: 'error',
};

function calcRate(baseRate: number, percentage: number) {
  return Math.round((baseRate * percentage / 100) * 100) / 100;
}

// ── TabPanel ─────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1: Rate Structure (Segment Percentages)
// ─────────────────────────────────────────────────────────────────────────────

function RateStructureTab() {
  const [staffRates, setStaffRates] = useState<BillingRate[]>([]);
  const [clientRates, setClientRates] = useState<BillingRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRate, setEditingRate] = useState<BillingRate | null>(null);
  const [editForm, setEditForm] = useState({
    percentage: '',
    hourlyRate: '',
    isActive: true,
    supportItemNumber: '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewBase, setPreviewBase] = useState<{ staff: number; client: number }>({ staff: 35.24, client: 68.00 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        billingRatesService.getAllRates('StaffPay'),
        billingRatesService.getAllRates('ClientCharge'),
      ]);
      if (s.success && s.data) setStaffRates(s.data);
      if (c.success && c.data) setClientRates(c.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (rate: BillingRate) => {
    setEditingRate(rate);
    setEditForm({
      percentage: rate.percentage.toString(),
      hourlyRate: rate.hourlyRate.toString(),
      isActive: rate.isActive,
      supportItemNumber: rate.supportItemNumber ?? '',
    });
  };

  const handleSave = async () => {
    if (!editingRate) return;
    setSaving(true);
    try {
      const pct = parseFloat(editForm.percentage);
      const hr = parseFloat(editForm.hourlyRate);
      await billingRatesService.updateRate(editingRate.id, {
        percentage: pct,
        hourlyRate: hr,
        isActive: editForm.isActive,
        // NDIS support-item-number — only meaningful for ClientCharge rates.
        // Claim type is per-shift on the roster, not on the rate.
        supportItemNumber: editForm.supportItemNumber.trim() || undefined,
      } as UpdateBillingRateRequest);
      setSuccess('Rate updated');
      setEditingRate(null);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onPercentageChange = (val: string) => {
    setEditForm(f => {
      const pct = parseFloat(val);
      const base = editingRate?.rateType === 'StaffPay' ? previewBase.staff : previewBase.client;
      const hr = isNaN(pct) ? f.hourlyRate : calcRate(base, pct).toString();
      return { ...f, percentage: val, hourlyRate: hr };
    });
  };

  const SegmentTable = ({ rates, rateType }: { rates: BillingRate[]; rateType: 'StaffPay' | 'ClientCharge' }) => {
    const base = rateType === 'StaffPay' ? previewBase.staff : previewBase.client;
    const isClient = rateType === 'ClientCharge';
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Segment</strong></TableCell>
              <TableCell><strong>Day Type</strong></TableCell>
              <TableCell><strong>Time Window</strong></TableCell>
              <TableCell align="right"><strong>Loading %</strong></TableCell>
              <TableCell align="right"><strong>Reference Rate</strong></TableCell>
              <TableCell align="right">
                <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-end">
                  <AutoCalcIcon fontSize="small" color="action" />
                  <span>If base = ${base.toFixed(2)}</span>
                </Stack>
              </TableCell>
              {isClient && <TableCell><strong>NDIS Support Item #</strong></TableCell>}
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Edit</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map(r => (
              <TableRow key={r.id} hover sx={r.isBaseRate ? { bgcolor: 'primary.50' } : {}}>
                <TableCell>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    {r.isBaseRate && <CheckIcon fontSize="small" color="primary" />}
                    <Typography variant="body2" fontWeight={r.isBaseRate ? 700 : 400}>{r.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell><Chip label={DAY_LABELS[r.dayType] ?? r.dayType} size="small" color={DAY_CHIP_COLOR[r.dayType] ?? 'default'} variant="outlined" /></TableCell>
                <TableCell><Typography variant="body2">{minutesToTimeString(r.timeStartMinutes)} – {minutesToTimeString(r.timeEndMinutes)}</Typography></TableCell>
                <TableCell align="right">
                  <Chip label={`${r.percentage.toFixed(2)}%`} size="small" color={r.isBaseRate ? 'primary' : 'default'} />
                </TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={600}>${r.hourlyRate.toFixed(2)}/hr</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">${calcRate(base, r.percentage).toFixed(2)}/hr</Typography>
                </TableCell>
                {isClient && (
                  <TableCell>
                    {r.supportItemNumber
                      ? <Chip label={r.supportItemNumber} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }} />
                      : <Typography variant="caption" color="error">— missing —</Typography>}
                  </TableCell>
                )}
                <TableCell><Chip label={r.isActive ? 'Active' : 'Inactive'} color={r.isActive ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell align="center">
                  <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(r)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rates.length === 0 && (
              <TableRow><TableCell colSpan={isClient ? 9 : 8} align="center"><Typography variant="body2" color="text.secondary" py={2}>No rate segments found. Run the migration to seed default rates.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }} icon={<PercentIcon />}>
        <strong>How percentage-based rates work:</strong> Each time segment has a loading percentage relative to the base (weekday daytime = 100%). When you set a staff member's or client's base rate, all other rates are automatically calculated as <em>base × percentage ÷ 100</em>. Update percentages here when rates change yearly.
      </Alert>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader title="Preview Calculator" subheader="Enter a base rate to see how all segments would calculate" sx={{ pb: 0 }} />
        <CardContent>
          <Stack direction="row" gap={3}>
            <TextField size="small" label="Staff base rate ($/hr)" value={previewBase.staff}
              onChange={e => setPreviewBase(p => ({ ...p, staff: parseFloat(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: 200 }} />
            <TextField size="small" label="Client base rate ($/hr)" value={previewBase.client}
              onChange={e => setPreviewBase(p => ({ ...p, client: parseFloat(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: 200 }} />
          </Stack>
        </CardContent>
      </Card>

      {loading ? <LinearProgress /> : (
        <Stack gap={4}>
          <Box>
            <Typography variant="h6" gutterBottom>Staff Pay Rate Structure</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>Support Worker Rate Card — Effective 1 July 2025 – 30 June 2026</Typography>
            <SegmentTable rates={staffRates} rateType="StaffPay" />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Client Charge Rate Structure</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>FeliceCare Price List — Effective 1 July 2025 – 30 June 2026</Typography>
            <SegmentTable rates={clientRates} rateType="ClientCharge" />
          </Box>
        </Stack>
      )}

      <Dialog open={!!editingRate} onClose={() => setEditingRate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Rate Segment — {editingRate?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              Changing the percentage updates the <strong>reference hourly rate</strong> below. Individual staff/client rates are always recalculated from their own base rate when generating invoices.
            </Alert>
            <TextField fullWidth label="Loading Percentage (%)" type="number"
              value={editForm.percentage}
              onChange={e => onPercentageChange(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText="100 = base rate. 103 = 3% loading above base." />
            <TextField fullWidth label="Reference Hourly Rate ($)" type="number"
              value={editForm.hourlyRate}
              onChange={e => setEditForm(f => ({ ...f, hourlyRate: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="Stored as reference. Actual rates use base × % ÷ 100." />

            {/* ── NDIS Support Catalogue mapping (ClientCharge rates) ──
                Stamped onto every invoice line generated from this rate.
                Claim type is captured per-shift on the roster, not here. */}
            {editingRate?.rateType === 'ClientCharge' && (
              <>
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  <strong>NDIS Support Item Number</strong> — appears on every invoice line
                  generated from this rate window. Find the code in the NDIS Support
                  Catalogue (the FELICE CARE price list lists each one against its day/time).
                </Alert>
                <TextField
                  fullWidth required
                  label="NDIS Support Item Number"
                  value={editForm.supportItemNumber}
                  onChange={e => setEditForm(f => ({ ...f, supportItemNumber: e.target.value }))}
                  placeholder="e.g. 01_011_0107_1_1"
                  helperText="Required on every NDIS invoice line. Edit any time."
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
              </>
            )}

            <FormControlLabel control={<Switch checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRate(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: Staff Rates (per-staff base rates)
// ─────────────────────────────────────────────────────────────────────────────

function StaffRatesTab() {
  const [allRates, setAllRates] = useState<StaffBaseRate[]>([]);
  const [staffRateSegments, setStaffRateSegments] = useState<BillingRate[]>([]);
  const [staffList, setStaffList] = useState<StaffProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedStaff, setExpandedStaff] = useState<number | null>(null);

  const [form, setForm] = useState<SetStaffBaseRateRequest>({
    staffId: 0,
    baseHourlyRate: 35.24,
    effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
    effectiveTo: undefined,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesResp, segmentsResp, staffResp] = await Promise.all([
        staffBaseRateService.getAll(),
        billingRatesService.getAllRates('StaffPay'),
        staffService.getStaffProfiles({ pageSize: 500, isActive: true }).catch(() => null),
      ]);
      if (ratesResp.success && ratesResp.data) setAllRates(ratesResp.data);
      if (segmentsResp.success && segmentsResp.data) setStaffRateSegments(segmentsResp.data);
      if (staffResp) {
        // The .NET endpoint returns either { results: [...] } (paginated) or
        // a flat array; tolerate both shapes.
        const list: StaffProfile[] = Array.isArray(staffResp)
          ? staffResp
          : (staffResp.results ?? (staffResp as any).items ?? []);
        setStaffList(list);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Quick lookup: internal staff PK → profile (for table display)
  const staffById = React.useMemo(() => {
    const m = new Map<number, StaffProfile>();
    for (const s of staffList) m.set(s.id, s);
    return m;
  }, [staffList]);

  // Same lookup but indexed by the numeric portion of `employeeId` so legacy
  // rate rows that were saved against the employee-id number (e.g. user typed
  // `20250001` from `EMP20250001`) still resolve to a real staff member.
  const staffByEmployeeIdNumeric = React.useMemo(() => {
    const m = new Map<number, StaffProfile>();
    for (const s of staffList) {
      const n = parseInt(String(s.employeeId || '').replace(/\D/g, ''), 10);
      if (Number.isFinite(n) && n > 0) m.set(n, s);
    }
    return m;
  }, [staffList]);

  const resolveStaff = (storedId: number): StaffProfile | undefined =>
    staffById.get(storedId) ?? staffByEmployeeIdNumeric.get(storedId);

  const formatStaffLabel = (s: StaffProfile) =>
    `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() + (s.employeeId ? ` (${s.employeeId})` : '');

  const byStaff = React.useMemo(() => {
    const map = new Map<number, StaffBaseRate[]>();
    for (const r of allRates) {
      if (!map.has(r.staffId)) map.set(r.staffId, []);
      map.get(r.staffId)!.push(r);
    }
    return map;
  }, [allRates]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ staffId: 0, baseHourlyRate: 35.24, effectiveFrom: format(new Date(), 'yyyy-MM-dd'), effectiveTo: undefined });
    setDialogOpen(true);
  };

  const openEdit = (rate: StaffBaseRate) => {
    setEditingId(rate.id);
    setForm({ staffId: rate.staffId, baseHourlyRate: rate.baseHourlyRate, effectiveFrom: rate.effectiveFrom.split('T')[0], effectiveTo: rate.effectiveTo?.split('T')[0] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await staffBaseRateService.updateRate(editingId, { baseHourlyRate: form.baseHourlyRate, effectiveFrom: form.effectiveFrom, effectiveTo: form.effectiveTo });
      } else {
        await staffBaseRateService.setRate(form);
      }
      setSuccess(editingId ? 'Rate updated' : 'Rate set');
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await staffBaseRateService.deleteRate(id);
      setSuccess('Rate deleted');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const staffIds = Array.from(byStaff.keys());

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Staff Base Rates</Typography>
          <Typography variant="body2" color="text.secondary">
            Set each staff member's base hourly rate. Other rates (evening, weekend, etc.) are automatically calculated from the rate structure percentages.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Set Staff Rate</Button>
      </Stack>

      {loading ? <LinearProgress /> : (
        staffIds.length === 0 ? (
          <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No staff rates set yet. Click "Set Staff Rate" to add the first one.</Typography>
          </Card>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell width={40} />
                  <TableCell><strong>Staff Member</strong></TableCell>
                  <TableCell align="right"><strong>Base Rate (Weekday Day)</strong></TableCell>
                  <TableCell><strong>Effective From</strong></TableCell>
                  <TableCell><strong>Effective To</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staffIds.map(staffId => {
                  const rates = byStaff.get(staffId) || [];
                  const active = rates.find(r => r.isActive) ?? null;
                  const isExpanded = expandedStaff === staffId;
                  const staff = resolveStaff(staffId);
                  const matchedByEmployeeId = !!staff && staff.id !== staffId;

                  return (
                    <React.Fragment key={staffId}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => setExpandedStaff(isExpanded ? null : staffId)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {staff ? (
                            <Stack spacing={0.25}>
                              <Typography variant="body2" fontWeight={600}>
                                {formatStaffLabel(staff)}
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  Stored Staff ID: {staffId}
                                </Typography>
                                {matchedByEmployeeId && (
                                  <Tooltip title={`This rate row was saved against the numeric portion of the employee ID (${staffId}) instead of the internal staff PK (${staff.id}). It still resolves correctly via fallback, but to avoid future ambiguity edit it and re-pick the staff member from the dropdown.`}>
                                    <Chip size="small" color="warning" variant="outlined"
                                      label={`Legacy key — actual PK ${staff.id}`} />
                                  </Tooltip>
                                )}
                              </Stack>
                            </Stack>
                          ) : (
                            <Stack spacing={0.25}>
                              <Typography variant="body2" fontWeight={600}>Staff #{staffId}</Typography>
                              <Typography variant="caption" color="error.main">
                                No matching staff profile found for ID {staffId}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {active ? (
                            <Typography variant="body2" fontWeight={700} color="primary.main">${active.baseHourlyRate.toFixed(2)}/hr</Typography>
                          ) : <Typography variant="body2" color="text.disabled">Not set</Typography>}
                        </TableCell>
                        <TableCell>{active ? format(new Date(active.effectiveFrom), 'dd/MM/yyyy') : '—'}</TableCell>
                        <TableCell>{active?.effectiveTo ? format(new Date(active.effectiveTo), 'dd/MM/yyyy') : 'Ongoing'}</TableCell>
                        <TableCell>
                          <Chip label={active?.isActive ? 'Active' : 'Inactive'} color={active?.isActive ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {active && (
                            <>
                              <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(active)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(active.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && active && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
                            <Collapse in={isExpanded}>
                              <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Calculated rates for {staff ? formatStaffLabel(staff) : `Staff #${staffId}`} (base = ${active.baseHourlyRate.toFixed(2)}/hr)
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                  {staffRateSegments.map(seg => (
                                    <Chip key={seg.id} label={`${seg.name}: $${calcRate(active.baseHourlyRate, seg.percentage).toFixed(2)}/hr (${seg.percentage.toFixed(2)}%)`}
                                      size="small" variant="outlined"
                                      color={seg.isBaseRate ? 'primary' : 'default'} />
                                  ))}
                                </Stack>
                                {rates.length > 1 && (
                                  <Box mt={1}>
                                    <Typography variant="caption" color="text.secondary">Rate history: {rates.length - 1} previous record(s)</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Staff Rate' : 'Set Staff Base Rate'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            {!editingId && (
              <Autocomplete
                fullWidth
                options={staffList}
                getOptionLabel={(s) => formatStaffLabel(s) || `Staff #${s.id}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={staffList.find(s => s.id === form.staffId) ?? null}
                onChange={(_, val) => setForm(f => ({ ...f, staffId: val?.id ?? 0 }))}
                renderOption={(props, s) => (
                  <li {...props} key={s.id}>
                    <Stack spacing={0}>
                      <Typography variant="body2">{`${s.firstName ?? ''} ${s.lastName ?? ''}`.trim()}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.employeeId} · Internal ID: {s.id}
                      </Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Staff Member"
                    helperText="Pick the staff member — the rate is keyed by the internal staff PK, not the employee ID."
                  />
                )}
                noOptionsText={staffList.length === 0 ? 'Loading staff…' : 'No matching staff'}
              />
            )}
            {editingId && (() => {
              const staff = resolveStaff(form.staffId);
              return (
                <Alert severity="info">
                  Editing rate for <strong>{staff ? formatStaffLabel(staff) : `Staff #${form.staffId}`}</strong>{' '}
                  (stored Staff ID: {form.staffId}{staff && staff.id !== form.staffId ? ` — actual internal PK is ${staff.id}` : ''}).
                </Alert>
              );
            })()}
            <TextField fullWidth required label="Base Hourly Rate (Weekday Daytime)" type="number"
              value={form.baseHourlyRate}
              onChange={e => setForm(f => ({ ...f, baseHourlyRate: parseFloat(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="This is the 100% reference rate. All other segments are calculated automatically." />

            {form.baseHourlyRate > 0 && staffRateSegments.length > 0 && (
              <Alert severity="success" icon={<AutoCalcIcon />}>
                <Typography variant="body2" gutterBottom><strong>Auto-calculated rates:</strong></Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {staffRateSegments.map(seg => (
                    <Chip key={seg.id} size="small" label={`${seg.name}: $${calcRate(form.baseHourlyRate, seg.percentage).toFixed(2)}`}
                      color={seg.isBaseRate ? 'success' : 'default'} variant={seg.isBaseRate ? 'filled' : 'outlined'} />
                  ))}
                </Stack>
              </Alert>
            )}

            <TextField fullWidth required label="Effective From" type="date"
              value={form.effectiveFrom}
              onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="Effective To (leave blank for ongoing)" type="date"
              value={form.effectiveTo ?? ''}
              onChange={e => setForm(f => ({ ...f, effectiveTo: e.target.value || undefined }))}
              InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.staffId || !form.baseHourlyRate}>
            {saving ? <CircularProgress size={18} /> : (editingId ? 'Update' : 'Set Rate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3: Client Rates (defaults + per-client)
// ─────────────────────────────────────────────────────────────────────────────

const SERVICE_TYPES = ['PersonalCare', 'HomeHelp', 'CommunityAccess'] as const;

function ClientRatesTab() {
  const [allRates, setAllRates] = useState<ClientBaseRate[]>([]);
  const [clientRateSegments, setClientRateSegments] = useState<BillingRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // NDIS catalogue rows for the service type currently being edited.
  // Loaded on demand when the dialog opens / service type changes.
  const [ndisItems, setNdisItems] = useState<NdisSupportItem[]>([]);

  const [form, setForm] = useState<SetClientBaseRateRequest & { isDefault: boolean }>({
    clientId: undefined,
    serviceType: 'PersonalCare',
    baseHourlyRate: 68.00,
    effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
    effectiveTo: undefined,
    isDefault: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesResp, segmentsResp] = await Promise.all([
        clientBaseRateService.getAll(),
        billingRatesService.getAllRates('ClientCharge'),
      ]);
      if (ratesResp.success && ratesResp.data) setAllRates(ratesResp.data);
      if (segmentsResp.success && segmentsResp.data) setClientRateSegments(segmentsResp.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defaultRates = allRates.filter(r => !r.clientId);
  const clientRates = allRates.filter(r => !!r.clientId);

  // Reload the NDIS catalogue rows for the form's currently-selected service
  // type whenever the dialog opens or the dropdown changes.
  useEffect(() => {
    if (!dialogOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await ndisSupportItemService.getAll(form.serviceType);
        if (!cancelled && r.success && r.data) setNdisItems(r.data);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load NDIS codes');
      }
    })();
    return () => { cancelled = true; };
  }, [dialogOpen, form.serviceType]);

  const openAdd = (isDefault = true) => {
    setEditingId(null);
    setForm({ clientId: undefined, serviceType: 'PersonalCare', baseHourlyRate: isDefault ? 68.00 : 0, effectiveFrom: format(new Date(), 'yyyy-MM-dd'), effectiveTo: undefined, isDefault });
    setDialogOpen(true);
  };

  const openEdit = (rate: ClientBaseRate) => {
    setEditingId(rate.id);
    setForm({ clientId: rate.clientId, serviceType: rate.serviceType, baseHourlyRate: rate.baseHourlyRate, effectiveFrom: rate.effectiveFrom.split('T')[0], effectiveTo: rate.effectiveTo?.split('T')[0], isDefault: !rate.clientId });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await clientBaseRateService.updateRate(editingId, { baseHourlyRate: form.baseHourlyRate, serviceType: form.serviceType, effectiveFrom: form.effectiveFrom, effectiveTo: form.effectiveTo });
      } else {
        await clientBaseRateService.setRate({ clientId: form.isDefault ? undefined : form.clientId, serviceType: form.serviceType, baseHourlyRate: form.baseHourlyRate, effectiveFrom: form.effectiveFrom, effectiveTo: form.effectiveTo });
      }
      setSuccess(editingId ? 'Rate updated' : 'Rate added');
      setDialogOpen(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await clientBaseRateService.deleteRate(id);
      setSuccess('Rate deleted');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }} icon={<MoneyIcon />}>
        <strong>Default rates</strong> (pre-populated from the FeliceCare Price List July 2025–2026) apply to all clients unless a client-specific override is set. You can set per-client rates below for clients with custom agreements.
      </Alert>

      {loading ? <LinearProgress /> : (
        <>
          {/* Default rates table */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Default Template Rates</Typography>
              <Typography variant="body2" color="text.secondary">Applied to all clients without a specific override (FeliceCare Price List)</Typography>
            </Box>
            <Button size="small" startIcon={<AddIcon />} onClick={() => openAdd(true)}>Update Default</Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Service Type</strong></TableCell>
                  <TableCell align="right"><strong>Base Rate (Weekday Day)</strong></TableCell>
                  <TableCell><strong>Effective From</strong></TableCell>
                  <TableCell><strong>Effective To</strong></TableCell>
                  <TableCell><strong>Calculated Rates</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defaultRates.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><Chip label={SERVICE_TYPE_LABELS[r.serviceType] ?? r.serviceType} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">${r.baseHourlyRate.toFixed(2)}/hr</Typography></TableCell>
                    <TableCell>{format(new Date(r.effectiveFrom), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{r.effectiveTo ? format(new Date(r.effectiveTo), 'dd/MM/yyyy') : 'Ongoing'}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {clientRateSegments.slice(0, 4).map(seg => (
                          <Chip key={seg.id} size="small" label={`${seg.name}: $${calcRate(r.baseHourlyRate, seg.percentage).toFixed(2)}`}
                            variant="outlined" color={seg.isBaseRate ? 'primary' : 'default'} />
                        ))}
                        {clientRateSegments.length > 4 && <Chip size="small" label={`+${clientRateSegments.length - 4} more`} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {defaultRates.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>No default rates found. Run the migration to seed defaults.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Client-specific overrides */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Client-Specific Rate Overrides</Typography>
              <Typography variant="body2" color="text.secondary">For clients with individually negotiated rates</Typography>
            </Box>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => openAdd(false)}>Add Client Override</Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Client ID</strong></TableCell>
                  <TableCell><strong>Service Type</strong></TableCell>
                  <TableCell align="right"><strong>Base Rate</strong></TableCell>
                  <TableCell><strong>Effective From</strong></TableCell>
                  <TableCell><strong>Effective To</strong></TableCell>
                  <TableCell><strong>Calculated Rates</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientRates.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>Client #{r.clientId}</TableCell>
                    <TableCell><Chip label={SERVICE_TYPE_LABELS[r.serviceType] ?? r.serviceType} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">${r.baseHourlyRate.toFixed(2)}/hr</Typography></TableCell>
                    <TableCell>{format(new Date(r.effectiveFrom), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{r.effectiveTo ? format(new Date(r.effectiveTo), 'dd/MM/yyyy') : 'Ongoing'}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {clientRateSegments.slice(0, 3).map(seg => (
                          <Chip key={seg.id} size="small" label={`${seg.name}: $${calcRate(r.baseHourlyRate, seg.percentage).toFixed(2)}`}
                            variant="outlined" color={seg.isBaseRate ? 'primary' : 'default'} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {clientRates.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" py={2}>No client-specific overrides. All clients use the default rates above.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Client Rate' : (form.isDefault ? 'Update Default Rate' : 'Add Client Rate Override')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            {!editingId && !form.isDefault && (
              <TextField fullWidth required label="Client ID" type="number"
                value={form.clientId ?? ''}
                onChange={e => setForm(f => ({ ...f, clientId: parseInt(e.target.value) || undefined }))}
                helperText="Enter the client's system ID" />
            )}
            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select value={form.serviceType} label="Service Type"
                onChange={e => setForm(f => ({ ...f, serviceType: e.target.value }))}>
                {SERVICE_TYPES.map(st => (
                  <MenuItem key={st} value={st}>{SERVICE_TYPE_LABELS[st]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField fullWidth required label="Base Hourly Rate (Weekday Daytime)" type="number"
              value={form.baseHourlyRate}
              onChange={e => setForm(f => ({ ...f, baseHourlyRate: parseFloat(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="100% reference rate. All other times calculated automatically." />

            {form.baseHourlyRate > 0 && clientRateSegments.length > 0 && (
              <Alert severity="success" icon={<AutoCalcIcon />}>
                <Typography variant="body2" gutterBottom><strong>Auto-calculated rates:</strong></Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {clientRateSegments.map(seg => (
                    <Chip key={seg.id} size="small"
                      label={`${seg.name}: $${calcRate(form.baseHourlyRate, seg.percentage).toFixed(2)}`}
                      color={seg.isBaseRate ? 'success' : 'default'}
                      variant={seg.isBaseRate ? 'filled' : 'outlined'} />
                  ))}
                </Stack>
              </Alert>
            )}

            {/* ── NDIS Support Item Codes (scoped to the selected service type) ──
                Each (service category × day window) maps to ONE NDIS Support
                Catalogue code. Personal Care, Home Help, and Community Access
                each have different codes for the same day window — see
                FELICE CARE price list. Edit inline; saves on blur. */}
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>NDIS Support Item Codes</strong>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  — for {SERVICE_TYPE_LABELS[form.serviceType] ?? form.serviceType}.
                  Stamped on every invoice line for shifts in this category.
                </Typography>
              </Typography>
              {ndisItems.length === 0 ? (
                <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                  No NDIS codes configured for this service type yet. Contact support
                  to seed the catalogue.
                </Alert>
              ) : (
                <Stack gap={1}>
                  {ndisItems.map(item => {
                    const windowLabel = item.dayType === 'Any'
                      ? 'Any time'
                      : item.timeStartMinutes === 0 && item.timeEndMinutes === 1440
                        ? item.dayType
                        : `${item.dayType} ${minutesToTimeString(item.timeStartMinutes)}–${minutesToTimeString(item.timeEndMinutes)}`;
                    return (
                      <Stack key={item.id} direction="row" gap={1} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 200 }}>{windowLabel}</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. 01_011_0107_1_1"
                          defaultValue={item.supportItemNumber}
                          inputProps={{ style: { fontFamily: 'monospace' } }}
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val === item.supportItemNumber) return;
                            try {
                              await ndisSupportItemService.update(item.id, {
                                serviceCategory: item.serviceCategory,
                                dayType: item.dayType,
                                timeStartMinutes: item.timeStartMinutes,
                                timeEndMinutes: item.timeEndMinutes,
                                supportItemNumber: val,
                                description: item.description,
                                isActive: item.isActive,
                              } as NdisSupportItemRequest);
                              setNdisItems(prev => prev.map(x =>
                                x.id === item.id ? { ...x, supportItemNumber: val } : x));
                              setSuccess(`NDIS code updated for ${windowLabel}`);
                            } catch (err: any) {
                              setError(err?.message ?? 'Failed to update NDIS code');
                            }
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <TextField fullWidth required label="Effective From" type="date"
              value={form.effectiveFrom}
              onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="Effective To (leave blank for ongoing)" type="date"
              value={form.effectiveTo ?? ''}
              onChange={e => setForm(f => ({ ...f, effectiveTo: e.target.value || undefined }))}
              InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.baseHourlyRate}>
            {saving ? <CircularProgress size={18} /> : (editingId ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4: Special Rates (Sleepover, Transport)
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success'> = {
  Sleepover: 'secondary',
  Transport: 'info',
};

function SpecialRatesTab() {
  const [rates, setRates] = useState<FlatRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    name: '', category: 'Sleepover', rateType: 'ClientCharge',
    ndisLineItem: '', unitRate: 0, unit: 'per night',
    isActive: true, effectiveFrom: '', effectiveTo: '',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await flatRateService.getAll();
      if (resp.success && resp.data) setRates(resp.data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (r: FlatRate) => {
    setEditingId(r.id);
    setForm({
      name: r.name, category: r.category, rateType: r.rateType,
      ndisLineItem: r.ndisLineItem ?? '', unitRate: r.unitRate, unit: r.unit,
      isActive: r.isActive,
      effectiveFrom: r.effectiveFrom?.slice(0, 10) ?? '',
      effectiveTo: r.effectiveTo?.slice(0, 10) ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: CreateFlatRateRequest = {
        name: form.name,
        category: form.category,
        rateType: form.rateType,
        ndisLineItem: form.ndisLineItem || undefined,
        unitRate: form.unitRate,
        unit: form.unit,
        isActive: form.isActive,
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || undefined,
      };
      if (editingId) {
        await flatRateService.update(editingId, payload as UpdateFlatRateRequest);
        setSuccess('Rate updated');
      } else {
        await flatRateService.create(payload);
        setSuccess('Rate added');
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await flatRateService.delete(id);
      setSuccess('Rate deleted');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Group by category
  const grouped = FLAT_RATE_CATEGORIES.reduce<Record<string, FlatRate[]>>((acc, cat) => {
    acc[cat] = rates.filter(r => r.category === cat);
    return acc;
  }, {} as Record<string, FlatRate[]>);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Special Rates</Typography>
          <Typography variant="body2" color="text.secondary">
            Fixed flat fees for sleepover shifts and travel reimbursements
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Add Rate</Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {FLAT_RATE_CATEGORIES.map(cat => (
        <Box key={cat} mb={4}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <Chip label={cat} color={CATEGORY_COLOR[cat] ?? 'default'} size="small" />
            <Typography variant="subtitle1" fontWeight={600}>{cat} Rates</Typography>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>NDIS Line Item</strong></TableCell>
                  <TableCell align="right"><strong>Rate</strong></TableCell>
                  <TableCell><strong>Unit</strong></TableCell>
                  <TableCell><strong>Effective From</strong></TableCell>
                  <TableCell><strong>Effective To</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped[cat]?.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                        label={r.rateType === 'StaffPay' ? 'Staff Pay' : 'Client Charge'}
                        color={r.rateType === 'StaffPay' ? 'default' : 'primary'} />
                    </TableCell>
                    <TableCell>
                      {r.ndisLineItem
                        ? <Typography variant="body2" fontFamily="monospace">{r.ndisLineItem}</Typography>
                        : <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        ${r.unitRate.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.unit}</TableCell>
                    <TableCell>{r.effectiveFrom ? format(new Date(r.effectiveFrom), 'dd/MM/yyyy') : '—'}</TableCell>
                    <TableCell>{r.effectiveTo ? format(new Date(r.effectiveTo), 'dd/MM/yyyy') : 'Ongoing'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.isActive ? 'Active' : 'Inactive'}
                        color={r.isActive ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {(grouped[cat]?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No {cat.toLowerCase()} rates configured.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Rate' : 'Add Special Rate'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField fullWidth required label="Name" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Assistance with Self-Care Activities – Night-Time Sleepover" />
            <Stack direction="row" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={form.category} label="Category"
                  onChange={e => setForm(f => ({
                    ...f, category: e.target.value,
                    unit: e.target.value === 'Sleepover' ? 'per night' : 'per kilometre',
                  }))}>
                  {FLAT_RATE_CATEGORIES.map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Rate Type</InputLabel>
                <Select value={form.rateType} label="Rate Type"
                  onChange={e => setForm(f => ({ ...f, rateType: e.target.value }))}>
                  <MenuItem value="ClientCharge">Client Charge</MenuItem>
                  <MenuItem value="StaffPay">Staff Pay</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField fullWidth label="NDIS Line Item" value={form.ndisLineItem}
              onChange={e => setForm(f => ({ ...f, ndisLineItem: e.target.value }))}
              placeholder="e.g. 01_010_0107_1_1" />
            <Stack direction="row" gap={2}>
              <TextField fullWidth required label="Unit Rate" type="number" value={form.unitRate}
                onChange={e => setForm(f => ({ ...f, unitRate: parseFloat(e.target.value) || 0 }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              <TextField fullWidth required label="Unit" value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="per night, per kilometre, etc." />
            </Stack>
            <Stack direction="row" gap={2}>
              <TextField fullWidth required label="Effective From" type="date"
                value={form.effectiveFrom}
                onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
              <TextField fullWidth label="Effective To" type="date"
                value={form.effectiveTo}
                onChange={e => setForm(f => ({ ...f, effectiveTo: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Stack>
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
              label="Active" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.unitRate || !form.effectiveFrom}>
            {saving ? <CircularProgress size={18} /> : (editingId ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

const BillingRatesPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <PageLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Billing Rates Management</Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Manage percentage-based rate structures, individual staff pay rates, and client charge rates.
          Rates are calculated as <strong>base rate × loading percentage ÷ 100</strong>.
        </Typography>

        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab icon={<PercentIcon />} iconPosition="start" label="Rate Structure" />
              <Tab icon={<PeopleIcon />} iconPosition="start" label="Staff Rates" />
              <Tab icon={<PersonIcon />} iconPosition="start" label="Client Rates" />
              <Tab icon={<HotelIcon />} iconPosition="start" label="Special Rates" />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            <TabPanel value={tab} index={0}><RateStructureTab /></TabPanel>
            <TabPanel value={tab} index={1}><StaffRatesTab /></TabPanel>
            <TabPanel value={tab} index={2}><ClientRatesTab /></TabPanel>
            <TabPanel value={tab} index={3}><SpecialRatesTab /></TabPanel>
          </Box>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default BillingRatesPage;
